// Scalona tabela struktury gości i przypisań

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import { GuestStructure as GuestStructureType } from "../../types/accommodation.types";

const GuestStructure: React.FC = () => {
  const {
    guestStructure,
    updateGuestStructure,
    assignedGuests,
    accommodationDays,
    currentDayIndex,
    addToAllDays,
  } = useAccommodation();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [localStructure, setLocalStructure] = useState(guestStructure);

  // Synchronize with context when it changes
  useEffect(() => {
    setLocalStructure(guestStructure);
  }, [guestStructure]);

  // Handle input changes - immediate local state update
  const handleInputChange = useCallback(
    (field: keyof GuestStructureType, value: number) => {
      const newStructure = { ...localStructure, [field]: value };
      setLocalStructure(newStructure);
    },
    [localStructure]
  );

  // Handle input blur - immediate API call
  const handleInputBlur = useCallback(
    (field: keyof GuestStructureType, value: number) => {
      // Clear any pending timeout (just in case)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Immediate API call on blur
      const newStructure = { ...localStructure, [field]: value };
      updateGuestStructure(newStructure);
    },
    [localStructure, updateGuestStructure]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Helper functions for room counts
  const getPairsRoomCount = () => Math.floor(assignedGuests.pairs / 2);
  const getSinglesRoomCount = () => Math.ceil(assignedGuests.singlesShared / 2);
  const getSinglesAloneRoomCount = () => assignedGuests.singlesAlone;

  // Helper function for progress calculation
  const calculateProgress = (assigned: number, required: number) => {
    if (required === 0) return 0;
    return Math.min(100, Math.round((assigned / required) * 100));
  };

  // Helper function for status
  const getStatus = (assigned: number, required: number) => {
    if (required === 0) return { icon: "—", color: "text-gray-400" };
    if (assigned === required) return { icon: "✓", color: "text-green-600" };
    if (assigned < required)
      return { icon: `⚠ -${required - assigned}`, color: "text-orange-600" };
    return { icon: `+${assigned - required}`, color: "text-red-600" };
  };

  const totalRequired =
    localStructure.pairs * 2 +
    localStructure.singles +
    localStructure.singlesAlone;
  const totalAssigned = assignedGuests.totalAssigned;
  const totalRooms =
    getPairsRoomCount() + getSinglesRoomCount() + getSinglesAloneRoomCount();

  return (
    <div className="bg-white rounded border">
      {/* Wskaźnik trybu */}
      <div className="px-3 py-2 border-b bg-gray-25">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            👥 Struktura gości
          </h3>
          <div className="text-xs">
            <div className="text-gray-600">
              Dzień{" "}
              {accommodationDays?.[currentDayIndex]?.dayNumber ||
                currentDayIndex + 1}
            </div>
            <div
              className={`font-medium ${
                addToAllDays ? "text-blue-600" : "text-green-600"
              }`}
            >
              {addToAllDays
                ? `Konfiguracja na ${
                    (accommodationDays?.length || 1) - 1
                  } dni jednocześnie`
                : `Konfiguracja tylko na Dzień ${
                    accommodationDays?.[currentDayIndex]?.dayNumber ||
                    currentDayIndex + 1
                  }`}
            </div>
          </div>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left py-2 px-3 font-medium">Typ</th>
            <th className="text-center py-2 px-3 font-medium">Ilość</th>
            <th className="text-center py-2 px-3 font-medium">Przypisano</th>
            <th className="text-center py-2 px-3 font-medium">Pokoje</th>
            <th className="text-center py-2 px-3 font-medium">Status</th>
            <th className="text-center py-2 px-3 font-medium">Postęp</th>
          </tr>
        </thead>
        <tbody>
          {/* Pary */}
          <tr className="border-t border-gray-100">
            <td className="py-2 px-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-pink-200 rounded-full"></span>
                👫 Pary
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <input
                type="number"
                min="0"
                value={localStructure.pairs}
                onChange={(e) =>
                  handleInputChange(
                    "pairs",
                    Math.max(0, parseInt(e.target.value, 10) || 0)
                  )
                }
                onBlur={(e) =>
                  handleInputBlur(
                    "pairs",
                    Math.max(0, parseInt(e.target.value, 10) || 0)
                  )
                }
                className="w-16 px-2 py-1 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </td>
            <td className="py-2 px-3 text-center">
              <div className="font-medium">
                {assignedGuests.pairs}/{localStructure.pairs * 2} os.
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <span className="font-medium">{getPairsRoomCount()} pok.</span>
            </td>
            <td className="py-2 px-3 text-center">
              <span
                className={`font-medium text-xs ${
                  getStatus(assignedGuests.pairs, localStructure.pairs * 2)
                    .color
                }`}
              >
                {getStatus(assignedGuests.pairs, localStructure.pairs * 2).icon}
              </span>
            </td>
            <td className="py-2 px-3 text-center">
              <div className="flex items-center gap-1">
                <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      localStructure.pairs === 0
                        ? "bg-gray-300"
                        : assignedGuests.pairs === localStructure.pairs * 2
                          ? "bg-green-500"
                          : assignedGuests.pairs > localStructure.pairs * 2
                            ? "bg-red-500"
                            : "bg-orange-500"
                    }`}
                    style={{
                      width: `${calculateProgress(
                        assignedGuests.pairs,
                        localStructure.pairs * 2
                      )}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 ml-1">
                  {calculateProgress(
                    assignedGuests.pairs,
                    localStructure.pairs * 2
                  )}
                  %
                </span>
              </div>
            </td>
          </tr>

          {/* Single */}
          <tr className="border-t border-gray-100">
            <td className="py-2 px-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-blue-200 rounded-full"></span>
                👤 Single
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <input
                type="number"
                min="0"
                value={localStructure.singles}
                onChange={(e) =>
                  handleInputChange(
                    "singles",
                    Math.max(0, parseInt(e.target.value, 10) || 0)
                  )
                }
                onBlur={(e) =>
                  handleInputBlur(
                    "singles",
                    Math.max(0, parseInt(e.target.value, 10) || 0)
                  )
                }
                className="w-16 px-2 py-1 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </td>
            <td className="py-2 px-3 text-center">
              <div className="font-medium">
                {assignedGuests.singlesShared}/{localStructure.singles} os.
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <span className="font-medium">{getSinglesRoomCount()} pok.</span>
            </td>
            <td className="py-2 px-3 text-center">
              <span
                className={`font-medium text-xs ${
                  getStatus(
                    assignedGuests.singlesShared,
                    localStructure.singles
                  ).color
                }`}
              >
                {
                  getStatus(
                    assignedGuests.singlesShared,
                    localStructure.singles
                  ).icon
                }
              </span>
            </td>
            <td className="py-2 px-3 text-center">
              <div className="flex items-center gap-1">
                <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      localStructure.singles === 0
                        ? "bg-gray-300"
                        : assignedGuests.singlesShared ===
                            localStructure.singles
                          ? "bg-green-500"
                          : assignedGuests.singlesShared >
                              localStructure.singles
                            ? "bg-red-500"
                            : "bg-orange-500"
                    }`}
                    style={{
                      width: `${calculateProgress(
                        assignedGuests.singlesShared,
                        localStructure.singles
                      )}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 ml-1">
                  {calculateProgress(
                    assignedGuests.singlesShared,
                    localStructure.singles
                  )}
                  %
                </span>
              </div>
            </td>
          </tr>

          {/* Single prywatnie */}
          <tr className="border-t border-gray-100">
            <td className="py-2 px-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-purple-200 rounded-full"></span>
                🔒 Single pryw.
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <input
                type="number"
                min="0"
                value={localStructure.singlesAlone}
                onChange={(e) =>
                  handleInputChange(
                    "singlesAlone",
                    Math.max(0, parseInt(e.target.value, 10) || 0)
                  )
                }
                onBlur={(e) =>
                  handleInputBlur(
                    "singlesAlone",
                    Math.max(0, parseInt(e.target.value, 10) || 0)
                  )
                }
                className="w-16 px-2 py-1 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </td>
            <td className="py-2 px-3 text-center">
              <div className="font-medium">
                {assignedGuests.singlesAlone}/{localStructure.singlesAlone} os.
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <span className="font-medium">
                {getSinglesAloneRoomCount()} pok.
              </span>
            </td>
            <td className="py-2 px-3 text-center">
              <span
                className={`font-medium text-xs ${
                  getStatus(
                    assignedGuests.singlesAlone,
                    localStructure.singlesAlone
                  ).color
                }`}
              >
                {
                  getStatus(
                    assignedGuests.singlesAlone,
                    localStructure.singlesAlone
                  ).icon
                }
              </span>
            </td>
            <td className="py-2 px-3 text-center">
              <div className="flex items-center gap-1">
                <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      localStructure.singlesAlone === 0
                        ? "bg-gray-300"
                        : assignedGuests.singlesAlone ===
                            localStructure.singlesAlone
                          ? "bg-green-500"
                          : assignedGuests.singlesAlone >
                              localStructure.singlesAlone
                            ? "bg-red-500"
                            : "bg-orange-500"
                    }`}
                    style={{
                      width: `${calculateProgress(
                        assignedGuests.singlesAlone,
                        localStructure.singlesAlone
                      )}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 ml-1">
                  {calculateProgress(
                    assignedGuests.singlesAlone,
                    localStructure.singlesAlone
                  )}
                  %
                </span>
              </div>
            </td>
          </tr>

          {/* Podsumowanie */}
          <tr className="border-t border-gray-200 bg-gray-25">
            <td className="py-2 px-3 font-medium text-gray-700">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-gray-400 rounded-full"></span>
                📊 Razem
              </div>
            </td>
            <td className="py-2 px-3 text-center font-medium text-blue-600"></td>
            <td className="py-2 px-3 text-center">
              <div className="font-medium text-blue-600">
                {totalAssigned}/{totalRequired} os.
              </div>
            </td>
            <td className="py-2 px-3 text-center">
              <span className="font-medium text-blue-600">
                {totalRooms} pok.
              </span>
            </td>
            <td className="py-2 px-3 text-center">
              <span
                className={`font-medium text-xs ${
                  getStatus(totalAssigned, totalRequired).color
                }`}
              >
                {getStatus(totalAssigned, totalRequired).icon}
              </span>
            </td>
            <td className="py-2 px-3 text-center">
              <div className="flex items-center gap-1">
                <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      totalRequired === 0
                        ? "bg-gray-300"
                        : totalAssigned === totalRequired
                          ? "bg-green-500"
                          : totalAssigned > totalRequired
                            ? "bg-red-500"
                            : "bg-orange-500"
                    }`}
                    style={{
                      width: `${calculateProgress(
                        totalAssigned,
                        totalRequired
                      )}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 ml-1 font-medium">
                  {calculateProgress(totalAssigned, totalRequired)}%
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default GuestStructure;
